import { useEffect, useState } from "react";
import { Laugh, Smile, Meh, Frown, Angry, Clock, LogOut, Loader2, Sparkles, Sun } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { EmployeePicker } from "./EmployeePicker";

const MOOD_ICON = { excelente: Laugh, bien: Smile, neutral: Meh, cansado: Frown, estresado: Angry };
const MOOD_COLOR = {
  excelente: "text-emerald-500 hover:bg-emerald-500/10 data-[on=true]:bg-emerald-500/15",
  bien: "text-sky-500 hover:bg-sky-500/10",
  neutral: "text-navy/50 hover:bg-navy/5",
  cansado: "text-gold hover:bg-gold/10",
  estresado: "text-destructive hover:bg-destructive/10",
};

function Shell({ children }) {
  return (
    <div className="fixed inset-0 z-[60] bg-navy/60 backdrop-blur-md flex items-center justify-center p-4" data-testid="daystart-overlay">
      <div className="bg-white rounded-3xl w-full max-w-md p-8 shadow-2xl animate-in fade-in zoom-in-95 duration-300">{children}</div>
    </div>
  );
}

export default function DayStart({ mandatory = false }) {
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState(null);   // mood | clockin | endday | overtime
  const [mood, setMood] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [ot, setOt] = useState({ responsable_id: "", motivo: "", franja_inicio: "", franja_fin: "" });

  const dismissedKey = (phase, date) => `daystart_${phase}_${date}`;

  const load = async () => {
    try {
      const { data } = await api.get("/daystart/status");
      setStatus(data);
      const skippedStart = !mandatory && localStorage.getItem(dismissedKey("start", data.date));
      if (data.needs_mood && !skippedStart) setStep("mood");
      else if (data.needs_clock_in && !skippedStart) setStep("clockin");
      else if (data.clocked_in && data.past_shift_end && !data.record?.overtime_authorized && !localStorage.getItem(dismissedKey("end", data.date))) setStep("endday");
      else setStep(null);
    } catch { setStep(null); }
  };
  useEffect(() => { load(); }, []);

  const dismissStart = () => { if (status) localStorage.setItem(dismissedKey("start", status.date), "1"); setStep(null); };
  const dismissEnd = () => { if (status) localStorage.setItem(dismissedKey("end", status.date), "1"); setStep(null); };

  const saveMood = async (m) => {
    setBusy(true);
    try {
      await api.post("/daystart/mood", { mood: m, note });
      setMood(m);
      if (status?.needs_clock_in) setStep("clockin"); else { toast.success("¡Gracias por compartir cómo te sientes!"); setStep(null); }
    } catch { toast.error("No se pudo registrar tu ánimo"); } finally { setBusy(false); }
  };

  const doClockIn = async () => {
    setBusy(true);
    let geo = "";
    try {
      geo = await new Promise((res) => {
        if (!navigator.geolocation) return res("");
        const t = setTimeout(() => res(""), 2500);
        navigator.geolocation.getCurrentPosition(
          (p) => { clearTimeout(t); res(`${p.coords.latitude.toFixed(4)},${p.coords.longitude.toFixed(4)}`); },
          () => { clearTimeout(t); res(""); }, { timeout: 2500 });
      });
    } catch { geo = ""; }
    try {
      await api.post("/daystart/clock-in", { device: navigator.userAgent.slice(0, 120), geo, platform: navigator.platform || "" });
      toast.success("Jornada iniciada. ¡Que tengas un gran día!");
      setStep(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo iniciar la jornada"); } finally { setBusy(false); }
  };

  const doClockOut = async () => {
    setBusy(true);
    try { await api.post("/daystart/clock-out"); toast.success("Jornada finalizada. ¡Buen descanso!"); setStep(null); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };

  const saveOvertime = async () => {
    if (!ot.responsable_id) return toast.error("Selecciona un responsable de RR.HH.");
    if (!ot.motivo.trim() || !ot.franja_inicio || !ot.franja_fin) return toast.error("Motivo y franja (inicio y fin) requeridos");
    setBusy(true);
    try { await api.post("/daystart/overtime", ot); toast.success("Horas extra autorizadas y registradas"); setStep(null); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };

  if (!step) return null;

  if (step === "mood") return (
    <Shell>
      <div className="text-center" data-testid="daystart-mood">
        <div className="w-14 h-14 rounded-2xl bg-gradient-violet flex items-center justify-center mx-auto mb-4"><Sun className="w-7 h-7 text-white" /></div>
        <h2 className="font-editorial text-3xl">Buen día</h2>
        <p className="text-sm text-navy/50 mt-1">¿Cómo te sientes hoy? Tu respuesta nos ayuda a cuidar el bienestar del equipo.</p>
        <div className="flex justify-center gap-2 mt-6">
          {status.moods.map((m) => { const Ic = MOOD_ICON[m]; return (
            <button key={m} data-testid={`daystart-mood-${m}`} disabled={busy} data-on={mood === m}
              onClick={() => saveMood(m)}
              className={`flex flex-col items-center gap-1 px-3 py-3 rounded-2xl transition-colors ${MOOD_COLOR[m]}`}>
              <Ic className="w-8 h-8" />
              <span className="text-[11px] text-navy/60">{status.mood_labels[m]}</span>
            </button>
          ); })}
        </div>
        <textarea className="field mt-4 text-sm" rows={2} placeholder="¿Algo que quieras compartir? (opcional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="daystart-mood-note" />
        {!mandatory && <button onClick={dismissStart} className="text-xs text-navy/50 hover:text-navy underline underline-offset-2 mt-4" data-testid="daystart-skip">Ahora no</button>}
      </div>
    </Shell>
  );

  if (step === "clockin") return (
    <Shell>
      <div className="text-center" data-testid="daystart-clockin">
        <div className="w-14 h-14 rounded-2xl bg-navy flex items-center justify-center mx-auto mb-4"><Clock className="w-7 h-7 text-cream" /></div>
        <h2 className="font-editorial text-3xl">Inicia tu jornada</h2>
        <p className="text-sm text-navy/50 mt-1">Registra tu entrada para comenzar el día. Se guardará fecha, hora, empresa y dispositivo.</p>
        <button onClick={doClockIn} disabled={busy} className="btn btn-primary w-full mt-6 gap-2" data-testid="daystart-clockin-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4" /> Iniciar Jornada</>}
        </button>
        {!mandatory && <button onClick={dismissStart} className="text-xs text-navy/40 hover:text-navy mt-4" data-testid="daystart-skip">Ahora no</button>}
      </div>
    </Shell>
  );

  if (step === "endday") return (
    <Shell>
      <div className="text-center" data-testid="daystart-endday">
        <div className="w-14 h-14 rounded-2xl bg-gold/20 flex items-center justify-center mx-auto mb-4"><LogOut className="w-7 h-7 text-gold" /></div>
        <h2 className="font-editorial text-3xl">Tu jornada terminó</h2>
        <p className="text-sm text-navy/50 mt-1">Tu horario finalizó a las {status.shift_end}. ¿Deseas registrar tu salida o continuar con horas extra?</p>
        <div className="flex flex-col gap-2 mt-6">
          <button onClick={doClockOut} disabled={busy} className="btn btn-primary w-full gap-2" data-testid="daystart-clockout-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><LogOut className="w-4 h-4" /> Finalizar Jornada</>}
          </button>
          <button onClick={() => setStep("overtime")} className="btn btn-ghost w-full" data-testid="daystart-overtime-btn">Continuar con Horas Extra</button>
        </div>
        <button onClick={dismissEnd} className="text-xs text-navy/40 hover:text-navy mt-4" data-testid="daystart-skip">Ahora no</button>
      </div>
    </Shell>
  );

  if (step === "overtime") return (
    <Shell>
      <div data-testid="daystart-overtime">
        <h2 className="font-editorial text-2xl text-center">Autorización de horas extra</h2>
        <p className="text-sm text-navy/50 mt-1 text-center">Requiere responsable de RR.HH., motivo y franja horaria (Cap. 4.5).</p>
        <div className="space-y-3 mt-5">
          <div><label className="text-xs font-medium text-navy/60 mb-1 block">Responsable de RR.HH.</label><EmployeePicker value={ot.responsable_id} onChange={(e) => setOt({ ...ot, responsable_id: e?.id || "" })} testid="daystart-ot-responsable" /></div>
          <textarea className="field" rows={2} placeholder="Motivo de las horas extra" value={ot.motivo} onChange={(e) => setOt({ ...ot, motivo: e.target.value })} data-testid="daystart-ot-motivo" />
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-navy/50">Inicio</label><input type="time" className="field" value={ot.franja_inicio} onChange={(e) => setOt({ ...ot, franja_inicio: e.target.value })} data-testid="daystart-ot-inicio" /></div>
            <div><label className="text-xs text-navy/50">Fin</label><input type="time" className="field" value={ot.franja_fin} onChange={(e) => setOt({ ...ot, franja_fin: e.target.value })} data-testid="daystart-ot-fin" /></div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5"><button onClick={() => setStep("endday")} className="btn btn-ghost">Volver</button><button onClick={saveOvertime} disabled={busy} className="btn btn-primary" data-testid="daystart-ot-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Autorizar"}</button></div>
      </div>
    </Shell>
  );

  return null;
}
